/**
 * Shrinking a photograph before it leaves the phone.
 *
 * The server resizes everything it receives, so this is not about the stored
 * file — it is about what crosses the network. A modern phone camera produces a
 * 4 MB photograph; the picture that ends up on a product tile is about 8 KB.
 * Uploading the 4 MB costs a shopkeeper real money on a prepaid bundle and takes
 * the better part of a minute on a bad signal, and every byte of it is discarded
 * on arrival.
 *
 * So the browser does the reduction first. 1280 pixels rather than the 640 the
 * server keeps: the extra headroom costs little at this size and means the
 * stored image is a downscale of something sharp rather than an upscale of
 * something already soft.
 *
 * This is a courtesy, not a control. The server validates and re-encodes
 * everything regardless — a client that skipped this step, or lied about what it
 * sent, changes nothing about what gets stored.
 */

const MAX_EDGE = 1280;
const QUALITY = 0.82;

export interface PreparedImage {
  blob: Blob;
  width: number;
  height: number;
  /** What the original weighed, so the interface can say what it saved. */
  originalBytes: number;
}

/**
 * Decode, rotate, downscale and re-encode.
 *
 * Falls back to the original file if anything goes wrong. A shopkeeper who has
 * just photographed a shelf should not be told "no" because a canvas failed —
 * the upload merely costs more, and the server will cope.
 */
export async function prepareImage(file: Blob): Promise<PreparedImage> {
  try {
    // `imageOrientation: "from-image"` applies the EXIF rotation tag. Without it
    // every photograph taken in portrait arrives on its side, and the tag is
    // lost the moment the canvas re-encodes.
    const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });

    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext("2d");
    if (!context) throw new Error("no 2d context");
    context.drawImage(bitmap, 0, 0, width, height);
    bitmap.close();

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/webp", QUALITY),
    );
    if (!blob) throw new Error("encode failed");

    // A tiny original re-encoded can come out *larger*. Keep whichever is
    // smaller — the point of the exercise is bytes on the wire.
    if (blob.size >= file.size && scale === 1) {
      return { blob: file, width, height, originalBytes: file.size };
    }

    return { blob, width, height, originalBytes: file.size };
  } catch {
    return { blob: file, width: 0, height: 0, originalBytes: file.size };
  }
}

/** Human-readable size, for the "3.9 MB → 180 KB" line under the preview. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
